import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Gift, Mail, Ban } from "lucide-react";
import { api, formatApiError, formatDate } from "../../lib/api";
import { Input } from "../ui/input";

const inviteLink = (code) => `${window.location.origin}/invite/${code}`;

const InviteRow = ({ inv, onRevoke }) => {
  const status = inv.used_by ? `Used by ${inv.used_by}` : inv.revoked ? "Switched off" : "Waiting";
  const open = !inv.used_by && !inv.revoked;
  const copy = async () => {
    await navigator.clipboard.writeText(inviteLink(inv.code));
    toast.success("Invite link copied");
  };
  const mail = `mailto:${inv.email}?subject=${encodeURIComponent("Your free year on extras.tv")}&body=${encodeURIComponent(`Hi ${inv.name || "there"},\n\nYou're invited to list yourself as a TV & film extra on extras.tv, free for your first year:\n${inviteLink(inv.code)}\n`)}`;
  return (
    <div className="border border-white/10 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`invite-row-${inv.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{inv.name || "Unnamed invite"}{inv.email && <span className="text-white/50 font-normal"> · {inv.email}</span>}</p>
        <p className="text-xs text-white/40 mt-1 truncate">{inviteLink(inv.code)} · created {formatDate(inv.created_at)}{inv.note && ` · ${inv.note}`}</p>
      </div>
      <span className={`text-xs uppercase tracking-widest font-bold ${inv.used_by ? "text-primary" : "text-white/50"}`} data-testid={`invite-status-${inv.id}`}>{status}</span>
      {open && (
        <div className="flex gap-2">
          <button onClick={copy} className="border border-white/15 px-3 py-2 text-sm inline-flex items-center gap-2 hover:border-primary hover:text-primary transition-colors" data-testid={`invite-copy-${inv.id}`}><Copy size={14} /> Copy link</button>
          {inv.email && <a href={mail} className="border border-white/15 px-3 py-2 text-sm inline-flex items-center gap-2 hover:border-primary hover:text-primary transition-colors" data-testid={`invite-email-${inv.id}`}><Mail size={14} /> Email</a>}
          <button onClick={() => onRevoke(inv)} className="border border-white/15 px-3 py-2 text-sm inline-flex items-center gap-2 hover:border-red-400 hover:text-red-400 transition-colors" data-testid={`invite-revoke-${inv.id}`}><Ban size={14} /> Switch off</button>
        </div>
      )}
    </div>
  );
};

export const InvitesPanel = () => {
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/invites").then((r) => setInvites(r.data));
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", form);
      await navigator.clipboard.writeText(inviteLink(data.code)).catch(() => {});
      toast.success("Invite created — link copied");
      setForm({ name: "", email: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (inv) => {
    await api.post(`/admin/invites/${inv.id}/revoke`);
    toast.success("Invite switched off");
    load();
  };

  return (
    <section className="mt-16" data-testid="admin-invites">
      <p className="label-xs flex items-center gap-2"><Gift size={12} className="text-primary" /> Special invites</p>
      <h2 className="font-display text-3xl font-bold mt-3">Invite someone free for a year</h2>
      <p className="text-white/60 text-sm mt-2 max-w-xl">Each link works once. The person registers on their own invite page and is listed free for 12 months, no card needed.</p>
      <form onSubmit={create} className="grid md:grid-cols-4 gap-3 mt-6" data-testid="invite-create-form">
        <Input placeholder="Their name (optional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name-input" />
        <Input type="email" placeholder="Their email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" />
        <Input placeholder="Note, e.g. 'met at Pinewood'" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="invite-note-input" />
        <button disabled={busy} className="bg-primary text-primary-foreground font-bold uppercase text-sm tracking-wider py-3 hover:bg-[hsl(43,100%,60%)] transition-colors disabled:opacity-60" data-testid="invite-create-button">
          {busy ? "Creating…" : "Create invite link"}
        </button>
      </form>
      <div className="space-y-2 mt-6" data-testid="invite-list">
        {invites.map((inv) => <InviteRow key={inv.id} inv={inv} onRevoke={revoke} />)}
        {!invites.length && <p className="text-white/40 text-sm" data-testid="invite-empty">No invites yet.</p>}
      </div>
    </section>
  );
};
